import { Button, Text, Tooltip } from '@payaca/ambrosia';
import {
  ROOF_TEMPLATE_CYCLE,
  ROOF_TEMPLATE_LABELS,
  type RoofDrawKind,
} from '@payaca/components/solarRoofView/SolarRoofView';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { FC, useState } from 'react';
import { DesignerTool } from './types';

type ToolItem = {
  tool: DesignerTool;
  icon: TIconName;
  label: string;
  key: string;
};

const MOD_KEY =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.userAgent)
    ? '⌘'
    : 'Ctrl';
const SELECTION_HINTS: [string[], string][] = [
  [['Click'], 'Select a roof, then click again for one panel'],
  [[MOD_KEY, 'Click'], 'Add or remove a panel'],
  [['Shift', 'Click'], 'Select a block of panels'],
  [[MOD_KEY, 'Shift', 'Click'], 'Add a block of panels'],
  [['Esc'], 'Clear the selection'],
  [['Delete'], 'Remove the selected panels from the design'],
];

const TOOLS: ToolItem[] = [
  { tool: 'select', icon: 'cursor-box.3', label: 'Select', key: 'V' },
  { tool: 'draw', icon: 'edit-05.3', label: 'Draw roof', key: 'Q' },
  { tool: 'obstacle', icon: 'x-square.3', label: 'Add obstacle', key: 'O' },
  {
    tool: 'trace',
    icon: 'building-02.3',
    label: 'Trace building outline',
    key: 'B',
  },
  { tool: 'pin', icon: 'marker-pin-01.3', label: 'Reposition pin', key: 'P' },
];

/** Drawing tools are abandoned; placement tools are finished. */
const EXIT_LABELS: Partial<Record<DesignerTool, string>> = {
  draw: 'Cancel drawing',
  obstacle: 'Cancel drawing',
  trace: 'Done',
  pin: 'Done',
};

/** Selection gestures, shown beside the armed Select tool. */
const SelectionShortcuts: FC = () => (
  <ul
    aria-label="Panel selection shortcuts"
    className="m-0 list-none space-y-0.5 p-0"
  >
    {SELECTION_HINTS.map(([keys, action]) => (
      <li key={action} className="flex items-baseline gap-2">
        <span className="flex shrink-0 gap-1">
          {keys.map((key) => (
            <kbd
              key={key}
              className="rounded border border-gray-500 px-1 font-sans"
            >
              <Text as="span">{key}</Text>
            </kbd>
          ))}
        </span>
        <Text as="span">{action}</Text>
      </li>
    ))}
  </ul>
);

type ToolRailProps = {
  tool: DesignerTool;
  setTool: (tool: DesignerTool) => void;
  hasFootprints: boolean;
  /** False on a hand-drawn roof, which has no analysis point to move. */
  canReposition?: boolean;
  layoutDisabledReason?: string;
  /** Shape drawn by Draw roof; its choices show under the armed row. */
  drawTemplate?: RoofDrawKind;
  setDrawTemplate?: (kind: RoofDrawKind) => void;
  /** Clears every roof, obstacle and panel and arms Draw roof (undoable). */
  onStartOver?: () => void;
  startOverDisabledReason?: string;
};

/** Labelled tool list at the top-left of the map; collapses to icons. */
const ToolRail: FC<ToolRailProps> = (props) => {
  const {
    tool,
    setTool,
    hasFootprints,
    canReposition = true,
    layoutDisabledReason,
    drawTemplate,
    setDrawTemplate,
    onStartOver,
    startOverDisabledReason,
  } = props;
  const [collapsed, setCollapsed] = useState(false);
  const tools = TOOLS.filter(
    (item) =>
      (item.tool !== 'trace' || hasFootprints) &&
      (item.tool !== 'pin' || canReposition)
  );
  const renderTool = (item: ToolItem) => {
    const active = tool === item.tool;
    // The armed tool stays live so it can always be switched off.
    const disabledReason = active ? undefined : layoutDisabledReason;
    // The armed tool exits from a small button at the end of its own row.
    const exit =
      active && !collapsed && item.tool !== 'select'
        ? EXIT_LABELS[item.tool]
        : undefined;
    const showShortcuts = active && !collapsed && item.tool === 'select';
    const row = (
      <Tooltip
        placement="right"
        className={exit || showShortcuts ? 'min-w-0 flex-1' : undefined}
        enabled={collapsed || !!disabledReason}
        tooltipContent={disabledReason ?? `${item.label} (${item.key})`}
      >
        <Button
          variant={active ? 'secondary' : 'quaternary'}
          size="sm"
          className={`${
            collapsed
              ? 'size-9 justify-center p-0'
              : 'w-full justify-start gap-2 px-2'
          } ${active ? 'border-l-2 border-l-primary' : ''}`}
          aria-label={item.label}
          aria-pressed={active}
          disabled={!!disabledReason}
          onClick={() => {
            if (active && item.tool === 'select') return;
            setTool(active ? 'select' : item.tool);
          }}
        >
          <UntitledIcon name={item.icon} className="size-5 shrink-0" />
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1 truncate text-left">
                {item.label}
              </span>
              <kbd
                aria-hidden
                className="rounded border border-gray-200 px-1 font-sans text-xs text-gray-500"
              >
                {item.key}
              </kbd>
            </>
          )}
        </Button>
      </Tooltip>
    );
    if (showShortcuts)
      return (
        <li key={item.tool}>
          <div className="flex items-center gap-1">
            {row}
            <Tooltip placement="right" tooltipContent={<SelectionShortcuts />}>
              <Button
                variant="quaternary"
                size="sm"
                className="size-8 shrink-0 justify-center p-0"
                aria-label="Panel selection shortcuts"
              >
                <UntitledIcon name="help-circle.3" className="size-5" />
              </Button>
            </Tooltip>
          </div>
        </li>
      );
    if (!exit) return <li key={item.tool}>{row}</li>;
    // The roof shape is chosen before the first click, so its choices sit
    // directly under the armed Draw roof row rather than in another task.
    const shapes =
      item.tool === 'draw' && drawTemplate && setDrawTemplate ? (
        <div
          role="radiogroup"
          aria-label="Roof shape"
          className="flex flex-wrap gap-1 pb-1 pl-2 pt-0.5"
        >
          {ROOF_TEMPLATE_CYCLE.map((kind) => (
            <Button
              key={kind}
              role="radio"
              aria-checked={drawTemplate === kind}
              variant={drawTemplate === kind ? 'secondary' : 'quaternary'}
              size="sm"
              className={`h-7 px-2 text-xs ${drawTemplate === kind ? 'border-primary' : ''}`}
              onClick={() => setDrawTemplate(kind)}
            >
              {ROOF_TEMPLATE_LABELS[kind]}
            </Button>
          ))}
        </div>
      ) : null;
    return (
      <li key={item.tool}>
        <div className="flex items-center gap-1">
          {row}
          <Button
            variant="quaternary"
            size="sm"
            className="size-8 shrink-0 justify-center p-0"
            aria-label={exit}
            onClick={() => setTool('select')}
          >
            <UntitledIcon name="x-close" className="size-4" />
          </Button>
        </div>
        {shapes}
      </li>
    );
  };
  return (
    <div
      aria-label="Designer tools"
      role="group"
      className={`rounded-lg border border-gray-200 bg-white p-1.5 shadow-sm ${collapsed ? 'w-fit' : 'w-max'}`}
    >
      <div
        className={`flex items-center pb-1 ${collapsed ? 'justify-center' : 'justify-between pl-2'}`}
      >
        {!collapsed && (
          <Text as="span">
            <strong>Tools</strong>
          </Text>
        )}
        <Tooltip
          placement="right"
          tooltipContent={collapsed ? 'Show tool names' : 'Hide tool names'}
        >
          <Button
            variant="quaternary"
            size="sm"
            className="size-8 justify-center p-0 focus:ring-0! focus-visible:ring-4!"
            aria-label={collapsed ? 'Expand tools' : 'Collapse tools'}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed(!collapsed)}
          >
            <UntitledIcon
              name="chevron-right-double.3"
              className={`size-4 ${collapsed ? '' : 'rotate-180'}`}
            />
          </Button>
        </Tooltip>
      </div>
      <ul className="m-0 list-none space-y-0.5 p-0">{tools.map(renderTool)}</ul>
      {onStartOver && (
        <>
          <div role="separator" className="my-1 h-px bg-gray-200" />
          <Tooltip
            placement="right"
            enabled={collapsed || !!startOverDisabledReason}
            tooltipContent={startOverDisabledReason ?? 'Start over'}
          >
            <Button
              variant="quaternary"
              size="sm"
              className={
                collapsed
                  ? 'size-9 justify-center p-0'
                  : 'w-full justify-start gap-2 px-2'
              }
              aria-label="Start over"
              disabled={!!startOverDisabledReason}
              onClick={onStartOver}
            >
              <UntitledIcon
                name="flip-backward.3"
                className="size-5 shrink-0"
              />
              {!collapsed && (
                <span className="min-w-0 flex-1 truncate text-left">
                  Start over
                </span>
              )}
            </Button>
          </Tooltip>
        </>
      )}
    </div>
  );
};

export default ToolRail;
